/**
 * Unified Authentication Page
 * Modern split-screen design with login, signup, and password reset
 */

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Lock, Mail, User, Building2, ArrowLeft, UserPlus } from "lucide-react";
import { z } from "zod";
import { config } from "@/lib/config";
import confeitariaImage from "/images/confeitaria-dark.jpg";

type AuthMode = "login" | "signup" | "reset";

// Validation schemas
const loginSchema = z.object({
  email: z.string().email("E-mail inválido"),
  password: z.string().min(1, "Senha é obrigatória"),
});

const createSignupSchema = (hasInvite: boolean) => z.object({
  fullName: z.string().trim().min(3, "Nome deve ter no mínimo 3 caracteres").max(100),
  cpf: hasInvite 
    ? z.string().optional()
    : z.string()
        .min(11, "CPF deve ter 11 dígitos")
        .regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{11}$/, "Formato inválido (XXX.XXX.XXX-XX)"),
  birthDate: z.string()
    .optional()
    .refine((date) => {
      if (!date) return true; // Opcional
      const birth = new Date(date);
      const age = (Date.now() - birth.getTime()) / (1000 * 60 * 60 * 24 * 365);
      return age >= 16;
    }, "Idade mínima: 16 anos"),
  email: z.string().email("E-mail inválido").max(255),
  password: z.string()
    .min(8, "Senha deve ter no mínimo 8 caracteres")
    .regex(/[A-Z]/, "Senha deve conter letra maiúscula")
    .regex(/[0-9]/, "Senha deve conter número"),
  businessName: hasInvite 
    ? z.string().optional()
    : z.string().trim().min(3, "Nome do negócio deve ter no mínimo 3 caracteres").max(100),
  acceptTerms: z.boolean().refine(val => val === true, {
    message: "Você deve aceitar os termos"
  })
});

const resetSchema = z.object({
  email: z.string().email("E-mail inválido"),
});

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const searchParams = new URLSearchParams(location.search);
  const initialMode = (searchParams.get("mode") as AuthMode) || "login";
  const inviteCode = searchParams.get("invite");
  
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [inviteData, setInviteData] = useState<{tenantId: string; role: string} | null>(null);
  
  // Login form
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  
  // Signup form
  const [signupData, setSignupData] = useState({
    fullName: "",
    cpf: "",
    birthDate: "",
    email: "",
    password: "",
    businessName: "",
    acceptTerms: false,
  });
  
  // Reset form
  const [resetEmail, setResetEmail] = useState("");
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/dashboard");
      }
    };
    checkAuth();
  }, [navigate]);

  // Validate invite code if present
  useEffect(() => {
    if (inviteCode && mode === "signup") {
      validateInviteCode();
    }
  }, [inviteCode, mode]);

  // Clear unnecessary fields when using invite
  useEffect(() => {
    if (inviteData) {
      setSignupData(prev => ({
        ...prev,
        cpf: "",
        birthDate: "",
        businessName: "",
      }));
    }
  }, [inviteData]);

  const validateInviteCode = async () => {
    if (!inviteCode) return;

    const { data, error } = await supabase.rpc("validate_invite_code", {
      p_invite_code: inviteCode,
    });

    if (error || !data || data.length === 0) {
      toast({
        title: "Código de convite inválido",
        description: "O código informado não é válido ou expirou",
        variant: "destructive",
      });
      return;
    }

    const invite = data[0];
    if (!invite.is_valid) {
      toast({
        title: "Convite inválido",
        description: invite.error_message,
        variant: "destructive",
      });
      return;
    }

    setInviteData({
      tenantId: invite.tenant_id,
      role: invite.role,
    });

    toast({
      title: "✅ Convite válido!",
      description: `Você será adicionado como ${invite.role === "funcionario" ? "Funcionário" : "Gestor"}`,
    });
  };

  // Password strength indicator
  const getPasswordStrength = (password: string): { strength: number; text: string; color: string } => {
    if (password.length === 0) return { strength: 0, text: "", color: "" };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    if (strength <= 2) return { strength: 1, text: "Fraca", color: "bg-red-500" };
    if (strength <= 3) return { strength: 2, text: "Média", color: "bg-yellow-500" };
    return { strength: 3, text: "Forte", color: "bg-green-500" };
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const validated = loginSchema.parse(loginData);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: validated.email,
        password: validated.password,
      });

      if (error) throw error;

      if (data.session) {
        toast({
          title: "Login realizado!",
          description: "Redirecionando...",
        });
        navigate("/dashboard");
      }
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          variant: "destructive",
          title: "Erro no login",
          description: error.message || "Verifique suas credenciais",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const signupSchema = createSignupSchema(!!inviteData);
      const validated = signupSchema.parse(signupData);
      
      const redirectUrl = `${window.location.origin}/`;

      // Create user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: validated.email,
        password: validated.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: validated.fullName,
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error("Usuário não foi criado");

      // Check if signup is via invite
      let finalTenantId: string;
      let finalRole: string;

      if (inviteData) {
        // Using invite: link to existing tenant
        finalTenantId = inviteData.tenantId;
        finalRole = inviteData.role;

        // Link user to tenant
        const { error: tenantUserError } = await supabase
          .from("tenant_users")
          .insert({
            user_id: authData.user.id,
            tenant_id: finalTenantId,
            is_owner: false,
          });

        if (tenantUserError) throw new Error("Erro ao vincular usuário ao negócio");

        // Assign role from invite
        const { error: roleError } = await supabase
          .from("tenant_user_roles")
          .insert({
            user_id: authData.user.id,
            tenant_id: finalTenantId,
            role: finalRole,
          });

        if (roleError) throw new Error("Erro ao atribuir permissões");

        // Mark invite as used
        if (inviteCode) {
          await supabase.rpc("mark_invite_used", {
            p_invite_code: inviteCode,
            p_user_id: authData.user.id,
          });
        }
      } else {
        // Normal signup: start trial via RPC (transacional)
        if (!validated.businessName) {
          throw new Error("Nome do negócio é obrigatório");
        }

        if (config.FEATURE_FLAGS.trialMode === "enabled") {
          // ✅ Trial automático via RPC (app_start_trial)
          // @ts-ignore - RPC app_start_trial foi recém criado, tipo será atualizado
          const { data: trialResult, error: trialError } = await supabase.rpc("app_start_trial", {
            p_user_id: authData.user.id,
            p_cpf: validated.cpf.replace(/\D/g, ''), // Remove formatação
            p_birth_date: validated.birthDate || null,
            p_business_name: validated.businessName,
          });

          if (trialError || !trialResult) {
            console.error("❌ Erro ao iniciar trial:", trialError);
            throw new Error(trialError?.message || "Erro ao iniciar período de trial");
          }

          // @ts-ignore
          const result = Array.isArray(trialResult) ? trialResult[0] : trialResult;
          // @ts-ignore
          if (!result.success) {
            // @ts-ignore
            console.error("❌ RPC retornou erro:", result.error_code, result.error_message);
            // @ts-ignore
            throw new Error(result.error_message || "Erro ao criar negócio");
          }

          // @ts-ignore
          finalTenantId = result.tenant_id;
        } else {
          // 🧯 FALLBACK: criar tenant manualmente (enquanto RPC não existe)
          console.warn("⚠️ trialMode=disabled: criando tenant sem trial automático");

          // Criar tenant com status pending_payment
          const { data: newTenant, error: tenantError } = await supabase
            .from("tenant")
            .insert({
              name: validated.businessName,
              subscription_status: "pending_payment",
            })
            .select()
            .single();

          if (tenantError || !newTenant) {
            throw new Error("Erro ao criar negócio");
          }

          finalTenantId = newTenant.id;

          // Vincular usuário como owner
          const { error: tenantUserError } = await supabase
            .from("tenant_users")
            .insert({
              user_id: authData.user.id,
              tenant_id: finalTenantId,
              is_owner: true,
            });

          if (tenantUserError) throw new Error("Erro ao vincular usuário ao negócio");

          // Atribuir role admin
          const { error: roleError } = await supabase
            .from("tenant_user_roles")
            .insert({
              user_id: authData.user.id,
              tenant_id: finalTenantId,
              role: "admin",
            });

          if (roleError) throw new Error("Erro ao atribuir permissões");
        }
      }

      toast({
        title: "✅ Conta criada com sucesso!",
        description: inviteData 
          ? "Você foi adicionado à equipe!" 
          : config.FEATURE_FLAGS.trialMode === "enabled"
          ? "Trial de 60 dias ativado! Explore todas as funcionalidades."
          : "Configure sua assinatura para começar a usar!",
      });

      navigate(inviteData ? "/dashboard" : "/dashboard");
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          variant: "destructive",
          title: "Erro ao criar conta",
          description: error.message || "Tente novamente mais tarde",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const validated = resetSchema.parse({ email: resetEmail });
      
      const { error } = await supabase.auth.resetPasswordForEmail(validated.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: "E-mail enviado!",
        description: "Verifique sua caixa de entrada para redefinir sua senha.",
      });

      setMode("login");
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          variant: "destructive",
          title: "Erro ao enviar e-mail",
          description: error.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = mode === "signup" ? getPasswordStrength(signupData.password) : null;

  return (
    <div className="flex min-h-screen bg-background">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img 
          src={confeitariaImage}
          alt="Confeitaria elegante"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent" />
        <div className="absolute bottom-12 left-12 text-white max-w-md z-10">
          <h2 className="text-4xl font-bold mb-4">
            Gerencie sua confeitaria com eficiência
          </h2>
          <p className="text-slate-200 text-lg">
            SisCuca — Controle total do seu negócio de confeitaria
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Logo/Header */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold">
              {mode === "login" && "Bem-vindo de volta"}
              {mode === "signup" && "Criar nova conta"}
              {mode === "reset" && "Recuperar senha"}
            </h1>
            <p className="text-muted-foreground">
              {mode === "login" && "Entre com suas credenciais para continuar"}
              {mode === "signup" && "Preencha os dados para começar"}
              {mode === "reset" && "Enviaremos um link de recuperação por e-mail"}
            </p>
          </div>

          {/* Login Form */}
          {mode === "login" && (
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-10"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    disabled={loading}
                  />
                </div>
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  />
                  <label
                    htmlFor="remember"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Lembrar deste computador
                  </label>
                </div>

                <Button
                  type="button"
                  variant="link"
                  className="px-0 text-sm"
                  onClick={() => setMode("reset")}
                >
                  Esqueci minha senha
                </Button>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Entrando..." : "Entrar"}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Não tem uma conta?{" "}
                <Button
                  type="button"
                  variant="link"
                  className="px-0 font-semibold"
                  onClick={() => setMode("signup")}
                >
                  Cadastre-se
                </Button>
              </p>
            </form>
          )}

          {/* Signup Form */}
          {mode === "signup" && (
            <form onSubmit={handleSignup} className="space-y-4">
              {/* Invite Banner */}
              {inviteData && (
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-4">
                  <div className="flex items-start gap-3">
                    <UserPlus className="h-5 w-5 text-primary mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">Convite ativo</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Você será adicionado como <strong>{inviteData.role === "funcionario" ? "Funcionário" : "Gestor"}</strong>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="fullName">Nome completo *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="João Silva"
                    className="pl-10"
                    value={signupData.fullName}
                    onChange={(e) => setSignupData({ ...signupData, fullName: e.target.value })}
                    disabled={loading}
                  />
                </div>
                {errors.fullName && <p className="text-sm text-destructive">{errors.fullName}</p>}
              </div>

              {/* CPF (obrigatório, formatação automática) */}
              {!inviteData && (
                <div className="space-y-2">
                  <Label htmlFor="cpf">CPF *</Label>
                  <Input
                    id="cpf"
                    type="text"
                    placeholder="000.000.000-00"
                    value={signupData.cpf}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 11) {
                        value = value.replace(/(\d{3})(\d)/, '$1.$2');
                        value = value.replace(/(\d{3})(\d)/, '$1.$2');
                        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                        setSignupData({ ...signupData, cpf: value });
                      }
                    }}
                    maxLength={14}
                    disabled={loading}
                  />
                  {errors.cpf && <p className="text-sm text-destructive">{errors.cpf}</p>}
                </div>
              )}

              {/* Data de Nascimento (opcional) */}
              {!inviteData && (
                <div className="space-y-2">
                  <Label htmlFor="birthDate">Data de Nascimento (opcional)</Label>
                  <Input
                    id="birthDate"
                    type="date"
                    value={signupData.birthDate}
                    onChange={(e) => setSignupData({ ...signupData, birthDate: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    disabled={loading}
                  />
                  <p className="text-xs text-muted-foreground">Idade mínima: 16 anos</p>
                  {errors.birthDate && <p className="text-sm text-destructive">{errors.birthDate}</p>}
                </div>
              )}

              {/* Hide business name when using invite */}
              {!inviteData && (
                <div className="space-y-2">
                  <Label htmlFor="businessName">Nome do negócio *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="businessName"
                      type="text"
                      placeholder="Minha Confeitaria"
                      className="pl-10"
                      value={signupData.businessName}
                      onChange={(e) => setSignupData({ ...signupData, businessName: e.target.value })}
                      disabled={loading}
                    />
                  </div>
                  {errors.businessName && <p className="text-sm text-destructive">{errors.businessName}</p>}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="signup-email">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-10"
                    value={signupData.email}
                    onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                    disabled={loading}
                  />
                </div>
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={signupData.password}
                    onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordStrength && signupData.password && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      <div className={`h-1 flex-1 rounded ${passwordStrength.strength >= 1 ? passwordStrength.color : 'bg-muted'}`} />
                      <div className={`h-1 flex-1 rounded ${passwordStrength.strength >= 2 ? passwordStrength.color : 'bg-muted'}`} />
                      <div className={`h-1 flex-1 rounded ${passwordStrength.strength >= 3 ? passwordStrength.color : 'bg-muted'}`} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Força da senha: {passwordStrength.text}
                    </p>
                  </div>
                )}
                {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
              </div>

              <div className="flex items-start space-x-2 pt-2">
                <Checkbox
                  id="terms"
                  checked={signupData.acceptTerms}
                  onCheckedChange={(checked) => setSignupData({ ...signupData, acceptTerms: checked as boolean })}
                />
                <label
                  htmlFor="terms"
                  className="text-sm leading-relaxed peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Aceito os{" "}
                  <a 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Termos de Serviço
                  </a>
                  {" "}e a{" "}
                  <a 
                    href="/privacy" 
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Política de Privacidade
                  </a>
                </label>
              </div>
              {errors.acceptTerms && <p className="text-sm text-destructive">{errors.acceptTerms}</p>}

              <Button type="submit" className="w-full" disabled={loading || !signupData.acceptTerms}>
                {loading ? "Criando conta..." : "Criar conta"}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Já tem uma conta?{" "}
                <Button
                  type="button"
                  variant="link"
                  className="px-0 font-semibold"
                  onClick={() => setMode("login")}
                >
                  Entrar
                </Button>
              </p>
            </form>
          )}

          {/* Reset Password Form */}
          {mode === "reset" && (
            <form onSubmit={handleReset} className="space-y-6">
              <Button
                type="button"
                variant="ghost"
                className="mb-4"
                onClick={() => setMode("login")}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar para login
              </Button>

              <div className="space-y-2">
                <Label htmlFor="reset-email">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-10"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    disabled={loading}
                  />
                </div>
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Enviando..." : "Enviar link de recuperação"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
